import React from 'react';
import type { Participant } from '@interactive-room';
import { computeStudentProgressPct, type ExamSessionState } from '@interactive-room';

interface ExamMonitorPanelProps {
  examSession: ExamSessionState | null | undefined;
  participants: Participant[];
  onUnlock?: (participantId: string) => void;
}

export function ExamMonitorPanel({ examSession, participants, onUnlock }: ExamMonitorPanelProps) {
  if (!examSession || examSession.status !== 'ACTIVE') return null;

  const students = participants.filter((p) => p.role === 'student');

  return (
    <div className="exam-monitor neo-monitor-panel pointer-events-auto absolute bottom-4 right-4 z-20 w-72 rounded-xl p-3">
      <p className="sala-label mb-1 font-black uppercase tracking-widest text-slate-950">Monitor evaluación</p>
      <p className="truncate font-display text-sm font-black text-slate-950">{examSession.title}</p>
      <p className="mb-3 text-[10px] font-bold uppercase tracking-wide text-slate-600">
        {examSession.advanceMode === 'AUTONOMOUS'
          ? 'Autónomo · cada alumno avanza solo'
          : `Bloque ${examSession.currentBlockIndex + 1} / ${examSession.totalBlocks}`}{' '}
        · {examSession.integrityMode === 'STRICT' ? '🔒 Estricto' : 'Flexible'}
      </p>
      <div className="max-h-40 space-y-2 overflow-y-auto">
        {students.length === 0 ? (
          <p className="text-xs font-medium text-slate-600">Sin alumnos conectados</p>
        ) : (
          students.map((p) => {
            const prog = examSession.progress[p.participantId];
            const pct = computeStudentProgressPct(prog);
            const locked = p.evaluationLocked || prog?.locked;
            const label = prog?.finished ? '✓ Listo' : `${pct}%`;
            return (
              <div key={p.participantId} className="exam-monitor__row">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs font-bold text-slate-950">{p.displayName}</span>
                  {locked ? (
                    <button
                      type="button"
                      className="text-[9px] font-black uppercase text-amber-700"
                      onClick={() => onUnlock?.(p.participantId)}
                    >
                      Desbloq
                    </button>
                  ) : (
                    <span className={`text-[10px] font-black${prog?.finished ? ' text-emerald-700' : ' text-slate-600'}`}>
                      {label}
                    </span>
                  )}
                </div>
                <div className="exam-monitor__bar mt-1">
                  <div className="exam-monitor__fill" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
